import {env} from '../config/env.js';
import {morphedRead} from './morphed-entity.service.js';
import {authedFetch} from '../auth/auth-fetch.js';

export interface AdminUser {
    id: string;            // = sub de Cognito (id morphed_user)
    email: string;
    name: string;
    status?: string;       // status de negocio (morphed_user)
    organizations?: string[];
    // joined desde Cognito:
    cognitoUsername?: string;
    enabled?: boolean;     // activo/deshabilitado en Cognito
    cognitoStatus?: string;// CONFIRMED / FORCE_CHANGE_PASSWORD / ...
}

interface CognitoUser {
    username: string;
    email: string;
    sub: string;
    enabled: boolean;
    status: string;
}

let _orgId: string | null = null;

/** Resuelve el id de la organización actual (env.MORPHED_ORG, ej. 'clam') una vez. */
export async function resolveOrgId(force = false): Promise<string> {
    if (_orgId && !force) return _orgId;
    try {
        const orgs = await morphedRead<{id: string; name: string}>('morphed-organization', {
            filters: [{name: env.MORPHED_ORG}],
            options: {limit: 1},
        });
        _orgId = orgs[0]?.id || '';
    } catch {
        _orgId = '';
    }
    return _orgId;
}

/** Users morphed_user de la org actual (con name, email, organizations). */
export async function loadOrgUsers(force = false): Promise<AdminUser[]> {
    const orgId = await resolveOrgId(force);
    if (!orgId) return [];
    try {
        const users = await morphedRead<AdminUser>('morphed-user', {
            filters: [{organizations: [orgId]}],
            options: {limit: 200},
        });
        return users || [];
    } catch {
        return [];
    }
}

/** Índice de usuarios Cognito (sub → {username, enabled, status}). Requiere morphed-admin. */
async function loadCognitoIndex(): Promise<Map<string, CognitoUser>> {
    const map = new Map<string, CognitoUser>();
    try {
        const res = await authedFetch(`${env.MORPHED_API_URL}/auth/users?limit=60`, {audience: 'morphed'});
        if (!res.ok) return map;
        const data = await res.json();
        for (const u of (data.users || []) as CognitoUser[]) {
            if (u.sub) map.set(u.sub, u);
        }
    } catch {}
    return map;
}

/** Lista combinada: morphed_user (datos de negocio) + Cognito (estado/username para acciones). */
export async function loadAdminUsers(): Promise<AdminUser[]> {
    const [orgUsers, cognito] = await Promise.all([loadOrgUsers(), loadCognitoIndex()]);
    return orgUsers.map((u) => {
        const c = cognito.get(u.id);
        return {
            ...u,
            cognitoUsername: c?.username,
            enabled: c?.enabled,
            cognitoStatus: c?.status,
        };
    });
}
